import React, { useState, useEffect, useCallback } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Funnel, Plus, Trash, FloppyDisk, Export, Eye, Pencil, X } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

interface FieldDef {
  name: string;
  label: string;
  group: string;
  computed?: boolean;
}

interface FieldGroup {
  group: string;
  fields: FieldDef[];
}

interface Config {
  id: number;
  name: string;
  description: string | null;
  format: 'csv' | 'xlsx';
  encoding: string;
  delimiter: string;
  filename_template: string;
  fields: string[];
  source_type: 'mub' | 'optin_cache';
  filters: any;
  created_at: string;
  updated_at: string;
}

export default function MubSelektion() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [configs, setConfigs] = useState<Config[]>([]);
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Editor
  const [editing, setEditing] = useState<Partial<Config> | null>(null);
  const [saving, setSaving] = useState(false);

  // Preview
  const [preview, setPreview] = useState<{ rows: any[]; total: number; fields: string[] } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewConfigId, setPreviewConfigId] = useState<number | null>(null);

  const loadConfigs = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/mub/export-configs'));
      const data = await res.json();
      setConfigs(Array.isArray(data) ? data.map((c: any) => ({
        ...c,
        fields: typeof c.fields === 'string' ? JSON.parse(c.fields) : c.fields,
        filters: typeof c.filters === 'string' ? JSON.parse(c.filters) : c.filters,
      })) : []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadFields = useCallback(async () => {
    try {
      const res = await authFetch(getApiUrl('/mub/export-configs/fields'));
      setFieldGroups(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadConfigs(); loadFields(); }, [loadConfigs, loadFields]);

  const handleSave = async () => {
    if (!editing || !editing.name || !editing.fields?.length) return;
    setSaving(true);
    try {
      const method = editing.id ? 'PUT' : 'POST';
      const url = editing.id
        ? getApiUrl(`/mub/export-configs/${editing.id}`)
        : getApiUrl('/mub/export-configs');
      await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editing),
      });
      setEditing(null);
      loadConfigs();
    } catch (err) {
      alert('Fehler: ' + (err as Error).message);
    }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Konfiguration löschen?')) return;
    await authFetch(getApiUrl(`/mub/export-configs/${id}`), { method: 'DELETE' });
    loadConfigs();
  };

  const handleExport = (id: number) => {
    // Direct download via window.open (auth via cookie not available, use fetch+blob)
    authFetch(getApiUrl(`/mub/export-configs/${id}/export`))
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const config = configs.find(c => c.id === id);
        a.download = (config?.filename_template || 'export')
          .replace('{name}', config?.name || 'export')
          .replace('{YYYY-MM-DD}', new Date().toISOString().slice(0, 10));
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const handlePreview = async (id: number) => {
    setPreviewLoading(true);
    setPreviewConfigId(id);
    try {
      const res = await authFetch(getApiUrl(`/mub/export-configs/${id}/preview`));
      setPreview(await res.json());
    } catch {
      setPreview(null);
    }
    setPreviewLoading(false);
  };

  const toggleField = (fieldName: string) => {
    if (!editing) return;
    const fields = editing.fields || [];
    setEditing({
      ...editing,
      fields: fields.includes(fieldName)
        ? fields.filter(f => f !== fieldName)
        : [...fields, fieldName],
    });
  };

  const setFilter = (key: string, value: any) => {
    if (!editing) return;
    const filters = { ...(editing.filters || {}) };
    if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete filters[key];
    } else {
      filters[key] = value;
    }
    setEditing({ ...editing, filters });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <SubPageHeader title="MuB Selektion" />
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
        <h1 style={{ color: '#003366', margin: '0 0 1.5rem 0', fontSize: isMobile ? '1.5rem' : '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Funnel size={28} weight="fill" /> MuB Selektion
        </h1>

        {/* Konfig-Liste */}
        {!editing && (
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{configs.length} Konfigurationen</span>
              <button onClick={() => setEditing({ name: '', fields: [], filters: {}, source_type: 'mub', format: 'csv', encoding: 'utf-8', delimiter: ';', filename_template: '{name}_{YYYY-MM-DD}.csv' })}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 16px', background: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                <Plus size={14} /> Neue Konfiguration
              </button>
            </div>

            {loading ? <div style={{ color: '#9ca3af' }}>Lade...</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f9fafb' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Beschreibung</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Felder</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Format</th>
                    <th style={{ padding: '8px', textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px', fontWeight: 600 }}>{c.name}</td>
                      <td style={{ padding: '8px', color: '#6b7280' }}>{c.description || '—'}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>{c.fields.length}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.75rem', background: c.format === 'xlsx' ? '#dbeafe' : '#f3f4f6' }}>
                          {c.format.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <button onClick={() => handlePreview(c.id)} title="Vorschau"
                          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                          <Eye size={14} />
                        </button>
                        <button onClick={() => handleExport(c.id)} title="Exportieren"
                          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer', color: '#059669' }}>
                          <Export size={14} />
                        </button>
                        <button onClick={() => setEditing({ ...c })} title="Bearbeiten"
                          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => handleDelete(c.id)} title="Löschen"
                          style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', background: 'white', cursor: 'pointer', color: '#dc2626' }}>
                          <Trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {configs.length === 0 && !loading && (
                    <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Keine Konfigurationen vorhanden</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Vorschau */}
        {preview && !editing && (
          <div style={{ marginTop: '1.5rem', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                Vorschau: {configs.find(c => c.id === previewConfigId)?.name} ({preview.total.toLocaleString('de-DE')} Datensätze)
              </span>
              <button onClick={() => setPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            {previewLoading ? <div style={{ color: '#9ca3af' }}>Lade...</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      {preview.fields.map(f => (
                        <th key={f} style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{f}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        {preview.fields.map(f => (
                          <td key={f} style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                            {row[f] !== null && row[f] !== undefined ? String(row[f]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Editor */}
        {editing && (
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#003366' }}>
                {editing.id ? 'Konfiguration bearbeiten' : 'Neue Konfiguration'}
              </h2>
              <button onClick={() => setEditing(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* Meta */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Name *</label>
                <input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Beschreibung</label>
                <input value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Dateiname</label>
                <input value={editing.filename_template || ''} onChange={e => setEditing({ ...editing, filename_template: e.target.value })}
                  placeholder="{name}_{YYYY-MM-DD}.csv"
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Datenquelle</label>
                <select value={editing.source_type || 'mub'} onChange={e => setEditing({ ...editing, source_type: e.target.value as any })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <option value="mub">MuB (Spieler-Bestand)</option>
                  <option value="optin_cache">OptIn Cache (Dialfire)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Format</label>
                <select value={editing.format || 'csv'} onChange={e => setEditing({ ...editing, format: e.target.value as any })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <option value="csv">CSV</option>
                  <option value="xlsx">XLSX</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Encoding</label>
                <select value={editing.encoding || 'utf-8'} onChange={e => setEditing({ ...editing, encoding: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <option value="utf-8">UTF-8</option>
                  <option value="latin1">Latin1</option>
                  <option value="iso-8859-15">ISO-8859-15</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>Trennzeichen</label>
                <select value={editing.delimiter || ';'} onChange={e => setEditing({ ...editing, delimiter: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem' }}>
                  <option value=";">Semikolon (;)</option>
                  <option value=",">Komma (,)</option>
                  <option value="	">Tab</option>
                </select>
              </div>
            </div>

            {/* Felder-Auswahl */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.9rem', color: '#003366', margin: '0 0 0.75rem 0' }}>
                Felder ({(editing.fields || []).length} ausgewählt)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '1rem' }}>
                {fieldGroups.map(g => (
                  <div key={g.group} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.75rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>
                      {g.group}
                    </div>
                    {g.fields.map(f => (
                      <label key={f.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', padding: '2px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={(editing.fields || []).includes(f.name)}
                          onChange={() => toggleField(f.name)} />
                        <span style={{ color: f.computed ? '#7c3aed' : '#374151' }}>{f.label}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Filter */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '0.9rem', color: '#003366', margin: '0 0 0.75rem 0' }}>Filter</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '0.75rem' }}>
                {editing.source_type === 'optin_cache' && (<>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Kampagne</label>
                  <select multiple value={editing.filters?.kampagne_typ || []}
                    onChange={e => setFilter('kampagne_typ', Array.from(e.target.selectedOptions, o => o.value))}
                    style={{ width: '100%', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', height: '80px' }}>
                    <option value="CB">CB (Callbacks)</option>
                    <option value="IB">IB (Inbound)</option>
                    <option value="KR">KR (KüRü)</option>
                    <option value="UG">UG (Upgrader)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Telefonie-Monat</label>
                  <input type="month" value={editing.filters?.telefon_monat || ''}
                    onChange={e => setFilter('telefon_monat', e.target.value || null)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }} />
                </div>
                </>)}
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Mandant</label>
                  <select multiple value={editing.filters?.mandant || []}
                    onChange={e => setFilter('mandant', Array.from(e.target.selectedOptions, o => o.value))}
                    style={{ width: '100%', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem', height: '60px' }}>
                    <option value="BOH">BOH (NKL)</option>
                    <option value="BOK">BOK (SKL)</option>
                    <option value="RLT">RLT (Rente)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Status</label>
                  <select value={editing.filters?.status || ''} onChange={e => setFilter('status', e.target.value || null)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <option value="">Alle</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="gekuendigt">Gekündigt</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Tel-OptIn</label>
                  <select value={editing.filters?.tel_optin ?? ''} onChange={e => setFilter('tel_optin', e.target.value === '' ? undefined : parseInt(e.target.value))}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <option value="">Egal</option>
                    <option value="1">Ja</option>
                    <option value="0">Nein</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Post-OptIn</label>
                  <select value={editing.filters?.post_optin ?? ''} onChange={e => setFilter('post_optin', e.target.value === '' ? undefined : parseInt(e.target.value))}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <option value="">Egal</option>
                    <option value="1">Ja</option>
                    <option value="0">Nein</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Email-OptIn</label>
                  <select value={editing.filters?.email_optin ?? ''} onChange={e => setFilter('email_optin', e.target.value === '' ? undefined : parseInt(e.target.value))}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }}>
                    <option value="">Egal</option>
                    <option value="1">Ja</option>
                    <option value="0">Nein</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Min. Alter</label>
                  <input type="number" value={editing.filters?.min_alter || ''} onChange={e => setFilter('min_alter', e.target.value ? parseInt(e.target.value) : null)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Max. Alter</label>
                  <input type="number" value={editing.filters?.max_alter || ''} onChange={e => setFilter('max_alter', e.target.value ? parseInt(e.target.value) : null)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.8rem' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Sonstiges</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.hat_telefon} onChange={e => setFilter('hat_telefon', e.target.checked || null)} />
                      Hat Telefon
                    </label>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.hat_email} onChange={e => setFilter('hat_email', e.target.checked || null)} />
                      Hat Email
                    </label>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.exclude_querulant} onChange={e => setFilter('exclude_querulant', e.target.checked || null)} />
                      Querulanten ausschließen
                    </label>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.exclude_merker_180} onChange={e => setFilter('exclude_merker_180', e.target.checked || null)} />
                      Merker 180 ausschließen (Tel verweigert)
                    </label>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.exclude_merker_500} onChange={e => setFilter('exclude_merker_500', e.target.checked || null)} />
                      Merker 500 ausschließen (Sperrliste)
                    </label>
                    <label style={{ fontSize: '0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input type="checkbox" checked={!!editing.filters?.mub_tel_optin_nicht_verweigert} onChange={e => setFilter('mub_tel_optin_nicht_verweigert', e.target.checked || null)} />
                      MuB Tel-OptIn nicht verweigert
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Speichern */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleSave} disabled={saving || !editing.name || !(editing.fields || []).length}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 20px', background: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', opacity: saving ? 0.6 : 1 }}>
                <FloppyDisk size={16} /> {saving ? 'Speichern...' : 'Speichern'}
              </button>
              <button onClick={() => setEditing(null)}
                style={{ padding: '10px 20px', background: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
